<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject } from "../lib/types";
  import { relativeDate } from "../lib/format";

  let { data, onproject }: { data: SurfaceSnapshot; onproject: (p: SurfaceProject) => void } = $props();

  const rank = (p: SurfaceProject) =>
    p.priority === "high" ? 0 : p.priority === "medium" ? 1 : 2;

  const sorted = $derived(
    [...data.projects]
      .filter((p) => p.status === "active")
      .sort((a, b) =>
        rank(a) - rank(b) ||
        (a.due ?? "9999").localeCompare(b.due ?? "9999") ||
        a.title.localeCompare(b.title),
      ),
  );
</script>

<div class="page">
  <header class="hero">
    <h1>Projects</h1>
    <p class="subtitle">{sorted.length} active</p>
  </header>

  <ul class="grouped-list">
    {#each sorted as p (p.slug)}
      <li>
        <button class="row" type="button" onclick={() => onproject(p)}>
          <div class="row-content">
            <div class="title-row">
              <span class="title">{p.title}</span>
              {#if p.priority === "high"}<span class="pill priority-high">high</span>{/if}
              {#if p.priority === "medium"}<span class="pill priority-medium">med</span>{/if}
            </div>
            {#if p.next_action}
              <div class="next">
                <span class="muted faint">next:</span> {p.next_action}
              </div>
            {/if}
            <div class="meta">
              {#if p.domain}
                <span class="dot {p.domain}"></span><span class="faint">{p.domain}</span><span class="sep">·</span>
              {/if}
              <span class="faint">{p.open_count} open</span>
              {#if p.waiting_count > 0}
                <span class="sep">·</span><span class="faint">{p.waiting_count} waiting</span>
              {/if}
              {#if p.due}
                <span class="sep">·</span><span class="faint">due {relativeDate(p.due)}</span>
              {/if}
            </div>
          </div>
          <svg class="chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
            <path d="M4.3 2.3a1 1 0 0 1 1.4 0l3 3a1 1 0 0 1 0 1.4l-3 3a1 1 0 1 1-1.4-1.4L6.6 6 4.3 3.7a1 1 0 0 1 0-1.4Z" fill="currentColor"/>
          </svg>
        </button>
      </li>
    {/each}
  </ul>

  {#if sorted.length === 0}
    <p class="empty">No active projects. Add one to <code>02-Projects/</code> with <code>status/active</code>.</p>
  {/if}
</div>

<style>
  .page { padding: 8px 16px 120px; max-width: 720px; margin: 0 auto; }
  .hero { padding: 12px 4px 16px; }
  h1 {
    font-size: 34px;
    line-height: 1.05;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
  .subtitle { color: var(--fg-dim); font-size: 15px; margin: 4px 0 0; }

  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list li { border-bottom: 0.5px solid var(--separator); }
  .grouped-list li:last-child { border-bottom: 0; }

  .row {
    display: flex;
    width: 100%;
    text-align: left;
    align-items: center;
    gap: 12px;
    padding: 14px 14px 14px 16px;
    transition: background 0.15s;
  }
  .row:active { background: var(--tap); }
  .row-content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .chevron { flex-shrink: 0; color: var(--fg-faint); }

  .title-row { display: flex; align-items: center; gap: 8px; }
  .title { font-size: 17px; font-weight: 500; flex: 1; min-width: 0; letter-spacing: -0.005em; }
  .next { font-size: 14px; color: var(--fg); line-height: 1.32; }
  .meta { font-size: 12px; color: var(--fg-dim); }
  .sep { margin: 0 6px; color: var(--fg-faint); }

  .empty { color: var(--fg-dim); padding: 20px 4px; font-size: 15px; }
  code {
    background: var(--bg-elev-2);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
  }
</style>
